import Image from "next/image";

export type LandingCmsImageProps = {
  src: string;
  alt: string;
  /** Intrinsic width — для CLS / srcset. */
  width: number;
  /** Intrinsic height — для CLS / srcset. */
  height: number;
  className?: string;
  sizes?: string;
  /** LCP / above-the-fold: без lazy. */
  priority?: boolean;
  /**
   * Заполнить родителя (position: relative | absolute).
   * width/height всё равно нужны как соотношение сторон для генерации srcset.
   */
  fill?: boolean;
};

/** Хосты из next.config images.remotePatterns (+ поддомены). */
const OPTIMIZABLE_HOST_SUFFIXES = [
  "storage.yandexcloud.net",
  "idealitika.ru",
  "dev.idealitika.ru",
] as const;

function isOptimizableCmsUrl(src: string): boolean {
  try {
    const url = new URL(src);
    if (url.protocol !== "http:" && url.protocol !== "https:") return false;
    const host = url.hostname.toLowerCase();
    return OPTIMIZABLE_HOST_SUFFIXES.some(
      (suffix) => host === suffix || host.endsWith(`.${suffix}`),
    );
  } catch {
    return false;
  }
}

/**
 * CMS/S3 картинки лендинга через next/image (resize, WebP/AVIF, width/height).
 * Неизвестный хост → обычный img с теми же width/height.
 */
export function LandingCmsImage({
  src,
  alt,
  width,
  height,
  className,
  sizes = "100vw",
  priority = false,
  fill = false,
}: LandingCmsImageProps) {
  if (!src) return null;

  if (!isOptimizableCmsUrl(src)) {
    /* eslint-disable-next-line @next/next/no-img-element -- external / non-S3 CMS URL */
    return (
      <img
        src={src}
        alt={alt}
        width={width}
        height={height}
        className={className}
        loading={priority ? "eager" : "lazy"}
        decoding="async"
        fetchPriority={priority ? "high" : undefined}
        style={
          fill
            ? { position: "absolute", inset: 0, width: "100%", height: "100%" }
            : undefined
        }
      />
    );
  }

  if (fill) {
    return (
      <Image
        src={src}
        alt={alt}
        fill
        sizes={sizes}
        className={className}
        priority={priority}
        loading={priority ? undefined : "lazy"}
      />
    );
  }

  return (
    <Image
      src={src}
      alt={alt}
      width={width}
      height={height}
      sizes={sizes}
      className={className}
      priority={priority}
      loading={priority ? undefined : "lazy"}
    />
  );
}
